<template>
  <pitchtype-mini-availability-calendar
    v-if="availabilityLoaded"
    :datepicker-availability-results="datepickerAvailability"
    :stay-dates="stayDates"
    :campsite-slug="campsiteSlug"
    :months-to-show="monthsToShow"
    :months-format="monthFormat"
    :calendar-start-date="calendarStartDate"
    :show-asterix="showAsterix"
  />
</template>

<script setup lang="ts">
import { useRoute } from 'nuxt/app'
import { computed, watch } from 'vue'
import { isEqual } from 'lodash-es'
import { calculateFourMonthRange } from '~/apps/calendar-unified/dateUtils'
import { useCalendarStore } from '~/stores/useCalendarStore'
import { DatepickerAvailabilityResultsDay } from '~/apps/datepicker-availability/types'
import { PitchtypeMiniAvailabilityCalendarStayDates } from './schemas'
import { SearchFiltersUrl } from '~/apps/url/search-filters/SearchFiltersUrl'

const props = withDefaults(
  defineProps<{
    campsiteSlug: string
    selectedCategoryIds?: string[]
    pitchtypeId: string
    stubData?: { month: number; year: number }
    stayDates?: PitchtypeMiniAvailabilityCalendarStayDates
    monthsToShow?: number
    monthFormat?: string
    calendarStartDate: string
    showAsterix?: boolean
    shouldFetch?: boolean
    datepickerAvailabilityResults?: DatepickerAvailabilityResultsDay[]
    hasNavigation?: boolean
  }>(),
  {
    selectedCategoryIds: () => [],
    datepickerAvailabilityResults: () => [],
    monthsToShow: 3,
    monthFormat: 'long',
    showAsterix: false,
    shouldFetch: true,
    hasNavigation: true,
  },
)

const route = useRoute()
const lang =
  (Array.isArray(route.params.lang)
    ? route.params.lang[0]
    : route.params.lang) || 'en-gb'
const calendarStore = useCalendarStore()

// Extract party parameters from URL consistently with main calendar
const party = computed(() => {
  const filters = SearchFiltersUrl.urlToFilters(route.query)
  // Sort child ages for consistent cache keys regardless of URL order
  const sortedChildAges =
    filters.party?.childAges?.slice().sort((a, b) => a - b) || []

  // Extract facet parameters (can be single value or array)
  const facetParam = route.query.facet
  const facets: string[] = Array.isArray(facetParam)
    ? facetParam.filter((f): f is string => typeof f === 'string')
    : facetParam && typeof facetParam === 'string'
      ? [facetParam]
      : []

  return {
    adults: filters.party?.adults || 2,
    dogs: filters.party?.dogs || 0,
    childAges: sortedChildAges,
    facets: facets.sort(), // Sort for consistent cache keys
  }
})

const todayMonthStart = () => {
  const today = new Date()
  return new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1))
}

const fetchWindow = computed(() => {
  const currentMonthStart = todayMonthStart()

  if (props.stayDates?.start) {
    const stayStart = new Date(props.stayDates.start)
    const stayMonthStart = new Date(
      Date.UTC(stayStart.getUTCFullYear(), stayStart.getUTCMonth(), 1),
    )

    const stayIsFuture = stayMonthStart > currentMonthStart
    let startMonth = stayIsFuture
      ? new Date(
          Date.UTC(stayStart.getUTCFullYear(), stayStart.getUTCMonth() - 1, 1),
        )
      : new Date(stayMonthStart)

    if (startMonth < currentMonthStart) {
      startMonth = currentMonthStart
    }

    return {
      center: props.stayDates.start,
      start: startMonth,
    }
  }

  if (props.calendarStartDate) {
    const calendarStart = new Date(props.calendarStartDate)
    let startMonth = new Date(
      Date.UTC(calendarStart.getUTCFullYear(), calendarStart.getUTCMonth(), 1),
    )

    if (startMonth < currentMonthStart) {
      startMonth = currentMonthStart
    }

    return {
      center: props.calendarStartDate,
      start: startMonth,
    }
  }

  return {
    center: undefined,
    start: currentMonthStart,
  }
})

const dateRange = computed(() => {
  if (props.hasNavigation) {
    return calculateFourMonthRange(fetchWindow.value.center)
  }

  const monthsToShow = props.monthsToShow ?? 3
  const startMonth = fetchWindow.value.start
  const start = new Date(
    Date.UTC(startMonth.getUTCFullYear(), startMonth.getUTCMonth(), 1),
  )
  const end = new Date(
    Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + monthsToShow, 1),
  )

  return { start, end }
})

// Component parameters for store
const componentParams = computed(() => ({
  pitchtypeId: props.pitchtypeId,
  campsiteSlug: props.campsiteSlug,
  stayDates: props.stayDates,
  party: party.value,
  facets: party.value.facets,
  dateRange: {
    start: dateRange.value.start.toISOString().split('T')[0],
    end: dateRange.value.end.toISOString().split('T')[0],
  },
}))

// Get cached data from store using high-level interface
const cachedDatepickerAvailability = computed(() => {
  if (!props.shouldFetch) return []
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- Store returns untyped calendar data
  return calendarStore.getComponentData(lang, componentParams.value)
})

// Generate cache key using store method
const cacheKey = computed(() => {
  if (!props.shouldFetch) {
    return `mini-calendar-props-${props.pitchtypeId}-${props.datepickerAvailabilityResults?.length || 0}`
  }
  return `calendar-mini-${calendarStore.generateComponentCacheKey(componentParams.value, lang)}`
})

const { data } = useAsyncData(
  cacheKey.value,
  async () => {
    // If shouldFetch is false, just return success - component uses prop data
    if (!props.shouldFetch) {
      return true
    }

    // Load data via store's high-level interface
    await calendarStore.loadComponentData(lang, componentParams.value)
    return true
  },
  {
    // Run on client so we reuse calendar store cache and avoid SSR fetch noise
    server: false,
    lazy: false,
    dedupe: 'cancel',
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- Nuxt payload data is untyped
    getCachedData: (key, nuxt) => nuxt.payload.data[key] ?? undefined,
  },
)

const availabilityLoaded = computed(() => {
  if (!props.shouldFetch) {
    // Using prop data, always loaded
    return true
  }

  // For unified API, loaded when useAsyncData fetch completed
  return !!data.value
})

const datepickerAvailability = computed(() => {
  if (!props.shouldFetch) {
    // Use provided prop data when not fetching
    return props.datepickerAvailabilityResults || []
  }

  // All data access comes from store for unified API
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- Store returns untyped calendar data
  return cachedDatepickerAvailability.value
})

// Watch for parameter changes to load calendar data (only if shouldFetch is true)
if (import.meta.client) {
  watch(
    [componentParams, () => props.shouldFetch],
    async ([newParams, shouldFetch], [oldParams, oldShouldFetch]) => {
      // Only load if shouldFetch is true and we need data
      if (!shouldFetch || !oldParams) {
        return
      }

      // Check if parameters actually changed
      if (isEqual(newParams, oldParams) && shouldFetch === oldShouldFetch) {
        return
      }

      // Ask store if it has the required data
      if (!calendarStore.hasComponentData(lang, newParams)) {
        await calendarStore.loadComponentData(lang, newParams)
      }
    },
    { deep: true },
  )
}
</script>
